import { z } from "zod";
import { email, password as passwordRule, phone, pin, uuid } from "./common";

/** Логин: email или телефон. */
export const loginIdentifier = z
  .string()
  .trim()
  .min(1, { error: "Введите email или телефон" })
  .max(254)
  .refine((v) => v.includes("@") ? email.safeParse(v).success : phone.safeParse(v).success, { error: "Введите email или телефон в формате +996…" });

export const loginSchema = z.strictObject({
  login: loginIdentifier,
  password: z.string().min(1, { error: "Введите пароль" }).max(128),
  /** Если логин найден в нескольких компаниях — клиент повторяет запрос с выбранной. */
  companyId: uuid.optional(),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const pinLoginSchema = z.strictObject({ pin });
export type PinLoginInput = z.infer<typeof pinLoginSchema>;

export const changePinSchema = z.strictObject({ currentPassword: z.string().min(1).max(128), pin });

export const deviceActivateSchema = z.strictObject({
  code: z.string().trim().toUpperCase().regex(/^[A-Z0-9]{8}$/, { error: "Код активации — 8 символов" }),
  platform: z.string().trim().max(50).optional(),
  appVersion: z.string().trim().max(30).optional(),
});

export type AuthUser = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  locale: string;
  companyId: string;
};

export type TokenPair = { accessToken: string; accessTokenExpiresAt: string };

export type LoginResult =
  | ({ status: "ok"; user: AuthUser } & TokenPair)
  | { status: "choose_company"; companies: { id: string; name: string }[] }
  /** 2FA (ТЗ 04 §3.1): пароль верный, нужен код из приложения — POST /auth/2fa/verify */
  | { status: "2fa_required"; challengeToken: string };

/** Код подтверждения email при регистрации: 6 цифр из письма (10 мин, 5 попыток). */
export const emailCode = z.string().trim().regex(/^\d{6}$/, { error: "Код — 6 цифр" });
/** Регистрация, шаг 1: отправить код на email (не чаще раза в минуту, ≤ 5 писем в час на адрес). */
export const registerCodeSchema = z.strictObject({ email });
export type RegisterCodeResult = { sent: true; resendAfterSec: number; expiresInSec: number };

/** Регистрация компании (самостоятельный старт trial), шаг 2: данные + код из письма. */
export const registerSchema = z
  .strictObject({
    companyName: z.string().trim().min(2, { error: "Минимум 2 символа" }).max(120),
    name: z.string().trim().min(2, { error: "Минимум 2 символа" }).max(120),
    email,
    phone: phone.optional(),
    password: z.string(),
    timezone: z.string().default("Asia/Bishkek"),
    currency: z.string().regex(/^[A-Z]{3}$/).default("KGS"),
    acceptTerms: z.literal(true, { error: "Необходимо принять условия оферты" }),
    code: emailCode,
  })
  .superRefine((v, ctx) => {
    const r = passwordRule.safeParse(v.password);
    if (!r.success) for (const i of r.error.issues) ctx.addIssue({ code: "custom", path: ["password"], message: i.message });
  });
export type RegisterInput = z.input<typeof registerSchema>;

export const DEVICE_TYPES = ["pos", "kds", "waiter", "kiosk", "customer_display", "ready_screen", "printer_bridge", "desktop", "courier"] as const;
/** Типы, учитываемые в limit.terminals (desktop — касса, привязанная по коду через /terminals/pair). */
export const TERMINAL_DEVICE_TYPES = ["pos", "kds", "waiter", "desktop"] as const;
/**
 * Как устройство работает после активации:
 * - pin — рабочее место сотрудника: вход по PIN, дальше его права (касса, официант, курьер);
 * - screen — общий экран без входа: устройство само получает доступ только к своему экрану (кухня, экран готовности);
 * - app — приложение Kafe Coco (привязка по коду в самом приложении, не в браузере).
 * Киоск, экран покупателя и принт-сервер пока не сделаны — создать их нельзя.
 */
export const DEVICE_ACCESS = {
  pos: "pin", waiter: "pin", courier: "pin",
  kds: "screen", ready_screen: "screen",
  desktop: "app",
} as const satisfies Partial<Record<(typeof DEVICE_TYPES)[number], "pin" | "screen" | "app">>;
export type DeviceAccess = (typeof DEVICE_ACCESS)[keyof typeof DEVICE_ACCESS];
export const CREATABLE_DEVICE_TYPES = Object.keys(DEVICE_ACCESS) as (keyof typeof DEVICE_ACCESS)[];
/** Экраны без PIN: тип → функция тарифа (экран готовности показывает заказы кухни). */
export const SCREEN_DEVICE_FEATURES = { kds: "core.kds", ready_screen: "core.kds" } as const;
export type ScreenDeviceType = keyof typeof SCREEN_DEVICE_FEATURES;
export const deviceAccess = (type: string): DeviceAccess | null => (DEVICE_ACCESS as Record<string, DeviceAccess>)[type] ?? null;
/**
 * Где принимается вход по PIN (`/auth/pin/login`): рабочие места, кухня (необязательный вход менеджера — вернуть тикет в работу;
 * десктоп-KDS) и касса-приложение. Экран готовности и несделанные типы — без PIN.
 */
export const pinLoginAllowed = (type: string) => deviceAccess(type) === "pin" || type === "kds" || type === "desktop";

export const deviceCreateSchema = z.strictObject({
  branchId: uuid,
  type: z.enum(CREATABLE_DEVICE_TYPES as [keyof typeof DEVICE_ACCESS, ...(keyof typeof DEVICE_ACCESS)[]]),
  name: z.string().trim().min(2).max(80),
});

/** Приглашение владельца/сотрудника: установка пароля по одноразовой ссылке (US-PA-03). */
export const inviteAcceptSchema = z.strictObject({ token: z.string().min(20).max(200), password: passwordRule });

/** Сброс пароля (ТЗ 04 §3.4): ответ на запрос всегда одинаковый; ссылка одноразовая, 30 мин. */
export const forgotPasswordSchema = z.strictObject({ email });
export const resetPasswordSchema = z.strictObject({
  token: z.string().min(20).max(200),
  password: passwordRule,
});
export type ResetPasswordInput = z.input<typeof resetPasswordSchema>;

/** 2FA TOTP: 6 цифр из приложения или резервный код xxxx-xxxx. */
const twoFactorCode = z.string().trim().toLowerCase().regex(/^(\d{6}|[a-z0-9]{4}-?[a-z0-9]{4})$/, { error: "Код — 6 цифр или резервный код" });
export const twoFactorVerifySchema = z.strictObject({ challengeToken: z.string().min(20).max(200), code: twoFactorCode });
export const twoFactorEnableSchema = z.strictObject({ code: z.string().trim().regex(/^\d{6}$/, { error: "Код — 6 цифр" }) });
export const twoFactorDisableSchema = z.strictObject({ password: z.string().min(1, { error: "Введите пароль" }).max(128), code: twoFactorCode });
